import * as Sentry from '@sentry/react'

import configureSentryScope from './configureSentryScope'
import dropThirdPartyEvents from './dropThirdPartyEvents'


// `store` is optional: clients that no longer use Redux have no state worth
// attaching to an event.
//
export default function initializeSentry({ store } = {}) {
  // eslint-disable-next-line no-process-env
  const { NODE_ENV } = process.env

  // Traffic to Sentry costs the company money, so we'll not generate
  // unnecessary expenditures here.
  if ( NODE_ENV === 'development' ) {
    return
  }

  const dsn = 'https://274009a3c5fc4c28a5ed9508fae2b9b1@o484749.ingest.sentry.io/5620248'

  Sentry.init({
    beforeSend: dropThirdPartyEvents,
    denyUrls: [
      // Google reCAPTCHA throws from inside its own script (obfuscated member
      // access, load timeouts). We embed it but don't control it. denyUrls
      // matches the URL of the frame that threw.
      /(gstatic|google)\.com\/recaptcha\//,
    ],
    dsn,
    environment: NODE_ENV,
    ignoreErrors: [
      // Injected by Microsoft Outlook's SafeLink / in-app browser, not our
      // code. It's a frameless UnhandledRejection, so dropThirdPartyEvents
      // can't attribute it by origin.
      /Object Not Found Matching Id:/,

      // A browser extension replaced the global XMLHttpRequest with something
      // that isn't a constructor, breaking any script that calls
      // `new XMLHttpRequest()`. Never originates in our code.
      /XMLHttpRequest is not a constructor/,

      // Page-translation tools (Google Translate and the like) mutate text
      // nodes behind React's back, so React then fails to remove or insert
      // around a node it no longer owns. Driven by the browser, not our render.
      /Failed to execute '(removeChild|insertBefore)' on 'Node'/,

      // Userflow (third-party product tours) failing to load its own module off
      // its CDN, or hitting its own client-side operation limit.
      /js\.userflow\.com/,
      /UserflowError/,

      // In-app browsers (Telegram, Android WebViews, etc.) inject a native
      // bridge and throw when the host method is missing or the native object
      // backing it has been torn down.
      /Error invoking (postEvent|postMessage)/,

      // A third-party script (often session replay) reached into a cross-origin
      // iframe and the browser blocked it.
      /Blocked a frame with origin/,

      // Globals defined only by injected browser-extension scripts. A reference
      // to one means the extension's code ran, not ours.
      /\b(xbrowser|swbrowser) is not defined\b/,

      // Axios raises this when no response ever arrived, which in practice
      // means the tab was closed or navigated away while the request was out,
      // the machine slept, or the connection dropped. Unlike everything above
      // it, this one is ours: only our own Axios instance can raise an
      // AxiosError, and that is why the type is matched alongside the message.
      // An extension that blocks a request produces `Failed to fetch` instead,
      // which `dropThirdPartyEvents` attributes by frame.
      //
      // This is dropped centrally rather than guarded at the call sites that
      // report it, because the stack is entirely Axios's own frames and so
      // reads the same whoever called it. Every caller lands in one Sentry
      // issue however careful an individual `catch` is, and callers that do
      // want their failures reported would refill that issue as soon as they
      // ship. A network-layer fault of our own is the cost: those are total
      // and immediate, though, so ops and support reach us long before a
      // warning-level report would.
      /^AxiosError: Network Error$/,
    ],
    integrations: [
      Sentry.replayIntegration(),
    ],
    maskAllText: false,
    release: '2.0.0',  // TODO: Use git hash from webpack during build?

    // Session Replay (https://docs.sentry.io/platforms/javascript/guides/react/session-replay/)
    //
    // We don't want to record any sessions unless they're throwing errors.
    replaysSessionSampleRate: 0,

    // We want to capture 100% of the sessions generating errors
    replaysOnErrorSampleRate: 1.0,
  })

  if ( store ) {
    Sentry.getCurrentScope(configureSentryScope(store))
  }
}
